import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchCollarSchema, fetchCollarValues, queryDatabaseHoles } from '../../services/drillingApi';
import { useHoleQueryStore } from '../../store/holeQueryStore';
import HoleLayerActions from './HoleLayerActions';
import {
  defaultOpForColumn,
  emptyGroup,
  emptyRule,
  isFreeTextColumn,
  isGroup,
  opIsMulti,
  opIsRange,
  opNeedsValue,
  operatorsForColumn,
  queryHasConstraint,
  sanitizeFilters,
  summarizeQuery,
} from '../../Utils/holeQuery';
import EditableGradeLegend, { LithLegendBody } from '../ui/EditableGradeLegend';
import { useClickStore } from '../../store/clickStore';
import {
  DEFAULT_HOLE_WIDTH_SCALE,
  MAX_HOLE_WIDTH_SCALE,
  MIN_HOLE_WIDTH_SCALE,
  useHoleDisplayStore,
} from '../../store/holeDisplayStore';

const inputClass =
  'w-full min-w-0 px-1.5 py-1 text-[11px] border border-yellow-600/25 rounded-sm bg-black/60 text-zinc-200 focus:outline-none focus:border-yellow-500/50 disabled:opacity-50';

function layerStatus(layerVisibility, name) {
  const s = layerVisibility?.[name];
  if (!s) return 'idle';
  if (s.visible && s.loadStatus === 'ready') return 'ready';
  if (s.visible && (s.loadStatus === 'loading' || !s.loadStatus)) return 'loading';
  if (!s.visible && (s.loadStatus === 'ready' || s.diskCached)) return 'cached';
  return s.loadStatus || 'idle';
}

function columnByName(schema, name) {
  return (schema?.columns || []).find((col) => col.name === name) || null;
}

function columnsByGroup(schema) {
  const order = schema?.groupOrder || [];
  const map = new Map(order.map((g) => [g, []]));
  (schema?.columns || []).forEach((col) => {
    const group = col.group || 'Other';
    if (!map.has(group)) map.set(group, []);
    map.get(group).push(col);
  });
  return Array.from(map.entries()).filter(([, cols]) => cols.length);
}

function LookupPicker({ options, multi, value, onChange, placeholder = 'Select…' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const selected = multi ? (Array.isArray(value) ? value.map(String) : []) : value ? [String(value)] : [];
  const selectedSet = new Set(selected);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((opt) =>
      `${opt.value} ${opt.label}`.toLowerCase().includes(needle)
    );
  }, [options, q]);

  const toggle = (nextValue) => {
    if (!multi) {
      onChange(nextValue);
      setOpen(false);
      return;
    }
    const next = selectedSet.has(nextValue)
      ? selected.filter((v) => v !== nextValue)
      : [...selected, nextValue];
    onChange(next);
  };

  const summary = selected.length
    ? selected.slice(0, 2).join(', ') + (selected.length > 2 ? ` +${selected.length - 2}` : '')
    : placeholder;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${inputClass} text-left flex items-center justify-between gap-1`}
      >
        <span className={`truncate ${selected.length ? 'text-zinc-200' : 'text-zinc-500'}`}>{summary}</span>
        <span className="text-zinc-500 shrink-0">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-0.5 left-0 right-0 max-h-44 overflow-hidden rounded-sm border border-yellow-600/30 bg-zinc-950 shadow-lg">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="w-full px-1.5 py-1 text-[11px] bg-black/70 border-b border-yellow-600/20 text-zinc-200 focus:outline-none"
          />
          <div className="max-h-36 overflow-y-auto">
            {filtered.map((opt) => {
              const on = selectedSet.has(String(opt.value));
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggle(String(opt.value))}
                  className={`m-0 w-full px-1.5 py-1 text-left text-[11px] flex items-center gap-1.5 ${
                    on ? 'bg-yellow-500/15 text-yellow-300' : 'text-zinc-300 hover:bg-yellow-500/5'
                  }`}
                >
                  <span className={`w-3 text-center shrink-0 ${on ? 'text-yellow-400' : 'text-zinc-600'}`}>
                    {on ? '✓' : '·'}
                  </span>
                  <span className="truncate">{opt.value}</span>
                  {opt.label && opt.label !== opt.value && (
                    <span className="truncate text-zinc-500">{opt.label}</span>
                  )}
                </button>
              );
            })}
            {!filtered.length && <p className="px-1.5 py-2 text-[11px] text-zinc-600">No matches</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function TypeaheadValues({ column, dataset, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(typeof value === 'string' ? value : '');
  const [options, setOptions] = useState([]);
  const timerRef = useRef(null);

  useEffect(() => {
    setQ(typeof value === 'string' ? value : '');
  }, [value]);

  useEffect(() => {
    if (!open || !column?.name) return undefined;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const data = await fetchCollarValues({ column: column.name, dataset, q, limit: 60 });
        setOptions(data.values || []);
      } catch {
        setOptions([]);
      }
    }, 220);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [open, column?.name, dataset, q]);

  return (
    <div className="relative">
      <input
        type="text"
        value={q}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQ(e.target.value);
          onChange(e.target.value);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={inputClass}
        placeholder={column?.name === 'Hole_ID' ? 'Hole ID…' : 'Value…'}
      />
      {open && options.length > 0 && (
        <div className="absolute z-30 mt-0.5 left-0 right-0 max-h-36 overflow-y-auto rounded-sm border border-yellow-600/30 bg-zinc-950 shadow-lg">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(opt.value);
                setQ(opt.value);
                setOpen(false);
              }}
              className="m-0 w-full px-1.5 py-1 text-left text-[11px] text-zinc-300 hover:bg-yellow-500/10 hover:text-yellow-300"
            >
              {opt.value}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ValueInput({ column, rule, dataset, onChange }) {
  if (!column || !opNeedsValue(rule.op)) return null;

  if (opIsRange(rule.op)) {
    const from = Array.isArray(rule.value) ? rule.value[0] : rule.value;
    const to = rule.valueTo ?? '';
    const type = column.uiType === 'date' ? 'date' : 'number';
    return (
      <div className="grid grid-cols-2 gap-1">
        <input type={type} value={from ?? ''} onChange={(e) => onChange({ value: e.target.value })} className={inputClass} />
        <input type={type} value={to} onChange={(e) => onChange({ valueTo: e.target.value })} className={inputClass} />
      </div>
    );
  }

  if (column.hasLookup && (column.values || []).length) {
    return (
      <LookupPicker
        options={column.values}
        multi={opIsMulti(rule.op)}
        value={rule.value}
        onChange={(value) => onChange({ value })}
      />
    );
  }

  if (column.uiType === 'number') {
    return (
      <input
        type="number"
        value={rule.value ?? ''}
        onChange={(e) => onChange({ value: e.target.value })}
        className={inputClass}
      />
    );
  }

  if (column.uiType === 'date') {
    return (
      <input
        type="date"
        value={rule.value ?? ''}
        onChange={(e) => onChange({ value: e.target.value })}
        className={inputClass}
      />
    );
  }

  if (!isFreeTextColumn(column.name) && (opIsMulti(rule.op) || rule.op === 'eq' || rule.op === 'starts_with')) {
    if (opIsMulti(rule.op)) {
      return (
        <input
          type="text"
          value={Array.isArray(rule.value) ? rule.value.join(', ') : rule.value || ''}
          onChange={(e) =>
            onChange({
              value: e.target.value
                .split(',')
                .map((v) => v.trim())
                .filter(Boolean),
            })
          }
          placeholder="Comma-separated values"
          className={inputClass}
        />
      );
    }
    return <TypeaheadValues column={column} dataset={dataset} value={rule.value} onChange={(value) => onChange({ value })} />;
  }

  return (
    <input
      type="text"
      value={rule.value ?? ''}
      onChange={(e) => onChange({ value: e.target.value })}
      className={inputClass}
    />
  );
}

function RuleEditor({ rule, schema, dataset, onChange, onRemove, canRemove }) {
  const column = columnByName(schema, rule.field);
  const grouped = columnsByGroup(schema);
  const ops = operatorsForColumn(column);

  const handleField = (name) => {
    const nextCol = columnByName(schema, name);
    const op = defaultOpForColumn(nextCol);
    onChange({
      field: name,
      op,
      value: nextCol?.hasLookup && opIsMulti(op) ? [] : '',
      valueTo: '',
    });
  };

  const handleOp = (op) => {
    let value = rule.value;
    if (opIsMulti(op) && !Array.isArray(value)) value = value ? [value] : [];
    if (!opIsMulti(op) && Array.isArray(value)) value = value[0] || '';
    if (!opNeedsValue(op)) value = '';
    onChange({ op, value, valueTo: opIsRange(op) ? rule.valueTo || '' : '' });
  };

  return (
    <div className="rounded-sm border border-yellow-600/20 bg-black/30 p-1.5 space-y-1">
      <div className="flex items-center gap-1">
        <select value={rule.field || ''} onChange={(e) => handleField(e.target.value)} className={inputClass}>
          <option value="">Column…</option>
          {grouped.map(([group, cols]) => (
            <optgroup key={group} label={group}>
              {cols.map((col) => (
                <option key={col.name} value={col.name}>
                  {col.name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="shrink-0 px-1.5 text-[10px] text-zinc-500 hover:text-red-400"
            title="Remove condition"
          >
            ✕
          </button>
        )}
      </div>
      {column && (
        <p className="text-[9px] uppercase tracking-wider text-zinc-500 px-0.5">
          {column.sqlType}
          {column.hasLookup ? ' · lookup' : ''}
          {column.nullable ? '' : ' · required'}
        </p>
      )}
      <select value={rule.op} onChange={(e) => handleOp(e.target.value)} disabled={!column} className={inputClass}>
        {ops.map((op) => (
          <option key={op.op} value={op.op}>
            {op.label}
          </option>
        ))}
      </select>
      <ValueInput column={column} rule={rule} dataset={dataset} onChange={onChange} />
    </div>
  );
}

function GroupEditor({ group, schema, dataset, onChange, onRemove }) {
  const updateRule = (idx, patch) => {
    const rules = group.rules.map((rule, i) => (i === idx ? { ...rule, ...patch } : rule));
    onChange({ rules });
  };
  return (
    <div className="rounded-sm border border-yellow-600/30 bg-yellow-500/5 p-1.5 space-y-1.5">
      <div className="flex items-center gap-1">
        <span className="text-[9px] uppercase tracking-wider text-zinc-500 flex-1">Group</span>
        <div className="flex rounded-sm overflow-hidden border border-yellow-600/25">
          {['AND', 'OR'].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onChange({ combinator: c })}
              className={`m-0 px-2 py-0.5 text-[9px] uppercase tracking-wider ${
                group.combinator === c ? 'bg-yellow-500/25 text-yellow-300' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              {c === 'AND' ? 'all' : 'any'}
            </button>
          ))}
        </div>
        <button type="button" onClick={onRemove} className="px-1 text-[10px] text-zinc-500 hover:text-red-400">
          ✕
        </button>
      </div>
      {group.rules.map((rule, idx) => (
        <RuleEditor
          key={rule.id || idx}
          rule={rule}
          schema={schema}
          dataset={dataset}
          onChange={(patch) => updateRule(idx, patch)}
          onRemove={() => onChange({ rules: group.rules.filter((_, i) => i !== idx) })}
          canRemove={group.rules.length > 1}
        />
      ))}
      <button
        type="button"
        onClick={() => onChange({ rules: [...group.rules, emptyRule()] })}
        className="m-0 w-full px-1.5 py-1 text-[10px] uppercase tracking-wider text-yellow-600/80 hover:text-yellow-400 border border-dashed border-yellow-600/25 rounded-sm"
      >
        + Condition in group
      </button>
    </div>
  );
}

function SavedFilterList({
  title,
  filters,
  appliedFilterId,
  onApply,
  onLoad,
  onDelete,
  onShare,
  emptyHint,
  defaultOpen = true,
  forceOpen = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const expanded = open || forceOpen;
  const count = filters.length;
  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="m-0 w-full flex items-center gap-1 px-0.5 py-0.5 text-left"
        title={expanded ? 'Collapse' : 'Expand'}
      >
        <span className="text-[9px] uppercase tracking-wider text-zinc-500 flex-1">{title}</span>
        {count > 0 && <span className="text-[9px] text-zinc-600">{count}</span>}
        <span className="text-[9px] text-zinc-600">{expanded ? '▴' : '▾'}</span>
      </button>
      {expanded && count > 0 && filters.map((filter) => {
        const active = appliedFilterId === filter.id;
        const holeType = (filter.rules || []).find((rule) => rule.field === 'Hole_Type');
        const holeTypeLabel = Array.isArray(holeType?.value) ? holeType.value.join(', ') : holeType?.value;
        const canDelete = typeof onDelete === 'function' && !filter.builtin && filter.mine !== false;
        return (
          <div
            key={filter.id}
            className={`flex items-center gap-1 rounded-sm border px-1.5 py-1 ${
              active ? 'border-yellow-500/50 bg-yellow-500/10' : 'border-yellow-600/15 bg-black/30'
            }`}
          >
            <button
              type="button"
              onClick={() => onApply(filter)}
              title="Pre-fill the form with this filter"
              className="m-0 flex-1 min-w-0 text-left"
            >
              <span className="block truncate text-[11px] text-zinc-200">{filter.name}</span>
              <span className="block truncate text-[9px] text-zinc-500">
                {filter.dataset || 'Any dataset'}
                {holeTypeLabel ? ` · ${holeTypeLabel}` : ''}
                {` · ${filter.intervalMode === 'lith1_code' ? 'lithology' : 'assay'}`}
                {` · ${filter.isPlan ? 'plan' : 'actual'}`}
              </span>
            </button>
            <button
              type="button"
              onClick={() => onLoad(filter)}
              className="m-0 shrink-0 h-5 px-1.5 rounded-sm text-[9px] uppercase tracking-wider border border-yellow-600/40 text-yellow-400 hover:bg-yellow-500/10"
              title="Load holes with this filter"
            >
              Load
            </button>
            {typeof onShare === 'function' && !filter.shared && !filter.builtin && (
              <button
                type="button"
                onClick={() => onShare(filter)}
                className="m-0 shrink-0 h-5 px-1.5 rounded-sm text-[9px] uppercase tracking-wider border border-yellow-600/40 text-yellow-400 hover:bg-yellow-500/10"
                title="Share with everyone on this site"
              >
                Share
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => onDelete(filter.id)}
                className="m-0 shrink-0 px-1 text-[10px] text-zinc-500 hover:text-red-400"
                title={filter.shared ? 'Remove shared filter' : 'Delete saved filter'}
              >
                ✕
              </button>
            )}
          </div>
        );
      })}
      {expanded && count === 0 && emptyHint && (
        <p className="text-[10px] text-zinc-600 px-0.5">{emptyHint}</p>
      )}
    </div>
  );
}

export default function LoadHolesForm({ layerVisibility, onToggleVisibility, searchText = '' }) {
  const schema = useHoleQueryStore((s) => s.schema);
  const schemaLoading = useHoleQueryStore((s) => s.schemaLoading);
  const schemaError = useHoleQueryStore((s) => s.schemaError);
  const draft = useHoleQueryStore((s) => s.draft);
  const loadedLayers = useHoleQueryStore((s) => s.loadedLayers);
  const savedFilters = useHoleQueryStore((s) => s.savedFilters);
  const sharedFilters = useHoleQueryStore((s) => s.sharedFilters);
  const builtinFilters = useHoleQueryStore((s) => s.builtinFilters);
  const appliedFilterId = useHoleQueryStore((s) => s.appliedFilterId);
  const filtersLoading = useHoleQueryStore((s) => s.filtersLoading);
  const setSchema = useHoleQueryStore((s) => s.setSchema);
  const setSchemaLoading = useHoleQueryStore((s) => s.setSchemaLoading);
  const setSchemaError = useHoleQueryStore((s) => s.setSchemaError);
  const patchDraft = useHoleQueryStore((s) => s.patchDraft);
  const resetDraft = useHoleQueryStore((s) => s.resetDraft);
  const applyFilter = useHoleQueryStore((s) => s.applyFilter);
  const saveFilter = useHoleQueryStore((s) => s.saveFilter);
  const shareFilter = useHoleQueryStore((s) => s.shareFilter);
  const deleteSavedFilter = useHoleQueryStore((s) => s.deleteSavedFilter);
  const addLoadedLayer = useHoleQueryStore((s) => s.addLoadedLayer);
  const removeLoadedLayer = useHoleQueryStore((s) => s.removeLoadedLayer);
  const holesLegendOpen = useHoleDisplayStore((s) => s.holesLegendOpen);
  const toggleHolesLegend = useHoleDisplayStore((s) => s.toggleHolesLegend);
  const desurveyEnabled = useHoleDisplayStore((s) => s.desurveyEnabled);
  const setDesurveyEnabled = useHoleDisplayStore((s) => s.setDesurveyEnabled);
  const showHoleLabels = useHoleDisplayStore((s) => s.showHoleLabels);
  const setShowHoleLabels = useHoleDisplayStore((s) => s.setShowHoleLabels);
  const holeWidthScale = useHoleDisplayStore((s) => s.holeWidthScale);
  const setHoleWidthScale = useHoleDisplayStore((s) => s.setHoleWidthScale);
  const widthLabel = `${Number(holeWidthScale.toFixed(2))}×`;
  const lithLegends = useClickStore((s) => s.lithLegends);

  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [saveName, setSaveName] = useState('');
  const [builderOpen, setBuilderOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const existing = useHoleQueryStore.getState().schema;
    if (!existing) setSchemaLoading(true);
    fetchCollarSchema()
      .then((data) => {
        if (!cancelled) setSchema(data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (useHoleQueryStore.getState().schema) setSchemaLoading(false);
        else setSchemaError(err.message || 'Failed to load collar columns');
      });
    return () => {
      cancelled = true;
    };
  }, [setSchema, setSchemaLoading, setSchemaError]);

  const filters = useMemo(
    () => sanitizeFilters(draft.combinator, draft.rules),
    [draft.combinator, draft.rules]
  );
  const canRun = queryHasConstraint(draft.dataset, filters);
  const defaultSaveName = summarizeQuery(draft.dataset, filters, draft.intervalMode, draft.isPlan);

  const filterMatchesSearch = (filter) => {
    const q = String(searchText || '').toLowerCase().trim();
    if (!q) return true;
    return (
      String(filter.name || '').toLowerCase().includes(q) ||
      String(filter.dataset || '').toLowerCase().includes(q)
    );
  };
  const myFilters = savedFilters.filter(filterMatchesSearch);
  const teamFilters = sharedFilters.filter(filterMatchesSearch);
  const minePresets = builtinFilters.filter(filterMatchesSearch);

  const applySaved = (filter) => {
    applyFilter(filter);
    setBuilderOpen(true);
    setPreview(null);
    setError(null);
  };

  const openNewFilter = () => {
    resetDraft();
    setSaveName('');
    setPreview(null);
    setError(null);
    setBuilderOpen(true);
  };

  const closeBuilder = () => {
    setBuilderOpen(false);
    setPreview(null);
    setError(null);
  };

  const loadFromFilter = (filter) => {
    const nextFilters = sanitizeFilters(filter.combinator, filter.rules);
    if (!queryHasConstraint(filter.dataset, nextFilters)) {
      setError('That saved filter has no dataset or conditions.');
      return;
    }
    applyFilter(filter);
    addLoadedLayer({
      dataset: filter.dataset,
      filters: nextFilters,
      intervalMode: filter.intervalMode,
      filterName: filter.name,
      filterId: filter.id,
      shared: !!filter.shared,
      isPlan: !!filter.isPlan,
    });
    setPreview(null);
    setError(null);
  };

  const persistFilter = async (shared) => {
    const label = saveName.trim() || defaultSaveName;
    setBusy(true);
    setError(null);
    try {
      if (shared) {
        await shareFilter(null, label);
      } else {
        await saveFilter(label, { shared: false });
      }
      setSaveName('');
    } catch (err) {
      setError(err.message || (shared ? 'Could not share filter' : 'Could not save filter'));
    } finally {
      setBusy(false);
    }
  };

  const shareExisting = async (filter) => {
    setBusy(true);
    setError(null);
    try {
      await shareFilter(filter);
    } catch (err) {
      setError(err.message || 'Could not share filter');
    } finally {
      setBusy(false);
    }
  };

  const removeFilter = async (id) => {
    setBusy(true);
    setError(null);
    try {
      await deleteSavedFilter(id);
    } catch (err) {
      setError(err.message || 'Could not delete filter');
    } finally {
      setBusy(false);
    }
  };

  const updateNode = (idx, patch) => {
    const rules = draft.rules.map((node, i) => (i === idx ? { ...node, ...patch } : node));
    patchDraft({ rules });
  };

  const removeNode = (idx) => {
    const rules = draft.rules.filter((_, i) => i !== idx);
    patchDraft({ rules: rules.length ? rules : [emptyRule()] });
  };

  const runPreview = useCallback(async () => {
    if (!canRun) {
      setError('Pick a dataset or add at least one condition.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await queryDatabaseHoles({
        dataset: draft.dataset,
        filters,
        intervalMode: draft.intervalMode,
        isplan: !!draft.isPlan,
        countOnly: true,
      });
      setPreview({ count: data.count || 0 });
    } catch (err) {
      setPreview(null);
      setError(err.message || 'Preview failed');
    } finally {
      setBusy(false);
    }
  }, [canRun, draft.dataset, draft.intervalMode, draft.isPlan, filters]);

  const appliedFilterName = useMemo(() => {
    const applied = [...savedFilters, ...sharedFilters, ...builtinFilters].find(
      (row) => row.id === appliedFilterId
    );
    return applied?.name || '';
  }, [appliedFilterId, savedFilters, sharedFilters, builtinFilters]);

  const holeLithCodes = useMemo(() => {
    const codes = [];
    const seen = new Set();
    Object.values(lithLegends || {}).forEach((list) => {
      (list || []).forEach((code) => {
        const key = String(code || '').trim();
        if (!key || seen.has(key)) return;
        seen.add(key);
        codes.push(key);
      });
    });
    return codes;
  }, [lithLegends]);

  const runLoad = useCallback(async () => {
    if (!canRun) {
      setError('Pick a dataset or add at least one condition.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      addLoadedLayer({
        dataset: draft.dataset,
        filters,
        intervalMode: draft.intervalMode,
        filterName: saveName.trim() || appliedFilterName,
        filterId: appliedFilterId || undefined,
        shared: [...sharedFilters].some((row) => row.id === appliedFilterId),
        isPlan: !!draft.isPlan,
      });
    } catch (err) {
      setError(err.message || 'Could not add hole query');
    } finally {
      setBusy(false);
    }
  }, [
    addLoadedLayer,
    appliedFilterId,
    appliedFilterName,
    canRun,
    draft.dataset,
    draft.intervalMode,
    draft.isPlan,
    filters,
    saveName,
    sharedFilters,
  ]);

  return (
    <div className="space-y-2 px-0.5 pb-2">
      <EditableGradeLegend
        title="Hole legend"
        overlayOpen={holesLegendOpen}
        onToggleOverlay={toggleHolesLegend}
      />
      <div className="rounded-sm border border-yellow-600/20 bg-black/30 px-1.5 py-1.5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-yellow-400">
            Hole width
          </span>
          <button
            type="button"
            title="Reset to default width"
            onClick={() => setHoleWidthScale(DEFAULT_HOLE_WIDTH_SCALE)}
            className="m-0 h-4 min-w-[1.75rem] px-1 rounded-sm border border-yellow-600/25 text-[9px] font-semibold text-yellow-500/90 hover:border-yellow-400 hover:bg-yellow-500/10"
          >
            {widthLabel}
          </button>
        </div>
        <input
          type="range"
          min={MIN_HOLE_WIDTH_SCALE}
          max={MAX_HOLE_WIDTH_SCALE}
          step="0.05"
          value={holeWidthScale}
          title={`Hole width ${widthLabel}. Assay and lithology scale with the traces.`}
          className="w-full h-3 m-0 accent-yellow-500"
          onChange={(e) => setHoleWidthScale(Number(e.target.value))}
        />
        <p className="mt-0.5 text-[8px] leading-snug text-zinc-500">
          Assay / lithology follow this size
        </p>
      </div>
      <label className="flex items-center gap-1.5 rounded-sm border border-yellow-600/20 bg-black/30 px-1.5 py-1.5 cursor-pointer">
        <input
          type="checkbox"
          checked={!!desurveyEnabled}
          onChange={(e) => setDesurveyEnabled(e.target.checked)}
          className="m-0 accent-yellow-500"
        />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-yellow-400">
          Desurvey holes
        </span>
        <span className="text-[9px] text-zinc-500">
          {desurveyEnabled ? 'Downhole survey (curved)' : 'Collar traces (straight)'}
        </span>
      </label>
      <label className="flex items-center gap-1.5 rounded-sm border border-yellow-600/20 bg-black/30 px-1.5 py-1.5 cursor-pointer">
        <input
          type="checkbox"
          checked={!!showHoleLabels}
          onChange={(e) => setShowHoleLabels(e.target.checked)}
          className="m-0 accent-yellow-500"
        />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-yellow-400">
          Hole IDs
        </span>
        <span className="text-[9px] text-zinc-500">Nearest collars</span>
      </label>
      {holeLithCodes.length > 0 && (
        <div className="rounded-sm border border-yellow-600/20 bg-black/30 px-1.5 py-1.5">
          <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Geology</p>
          <LithLegendBody codes={holeLithCodes} />
        </div>
      )}
      {!builderOpen ? (
        <button
          type="button"
          onClick={openNewFilter}
          className="m-0 w-full px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm border border-yellow-600/40 text-yellow-400 hover:bg-yellow-500/10"
        >
          + Add filter
        </button>
      ) : (
        <>
      <div className="rounded-sm border border-yellow-600/20 bg-black/30 px-1.5 py-1.5 space-y-1.5">
        <div className="flex items-center gap-1">
          <p className="text-[9px] uppercase tracking-wider text-zinc-500 flex-1">
            {appliedFilterId ? 'Edit filter' : 'New filter'}
          </p>
          <button
            type="button"
            onClick={closeBuilder}
            className="m-0 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-zinc-500 hover:text-zinc-300"
          >
            Done
          </button>
        </div>

        <label className="block">
          <span className="block text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5">Dataset</span>
          <select
            value={draft.dataset}
            onChange={(e) => {
              patchDraft({ dataset: e.target.value });
              setPreview(null);
            }}
            disabled={schemaLoading}
            className={inputClass}
          >
            <option value="">Any dataset</option>
            {(schema?.datasets || []).map((ds) => (
              <option key={ds.value} value={ds.value}>
                {ds.value}
                {ds.label && ds.label !== ds.value ? ` — ${ds.label}` : ''}
                {ds.IsCurrent ? ' (current)' : ''}
              </option>
            ))}
          </select>
          {!draft.dataset && (
            <span className="block text-[9px] text-zinc-600 mt-0.5">
              A dataset uses the collar index and loads faster.
            </span>
          )}
        </label>

        <div className="grid grid-cols-2 gap-1.5">
          <label className="block">
            <span className="block text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5">Intervals</span>
            <select
              value={draft.intervalMode}
              onChange={(e) => patchDraft({ intervalMode: e.target.value })}
              className={inputClass}
              disabled={!!draft.isPlan}
            >
              <option value="assay">Assay</option>
              <option value="lith1_code">Lithology</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5">Match</span>
            <select
              value={draft.combinator}
              onChange={(e) => patchDraft({ combinator: e.target.value })}
              className={inputClass}
            >
              <option value="AND">All conditions</option>
              <option value="OR">Any condition</option>
            </select>
          </label>
        </div>
        <div>
          <span className="block text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5">Holes</span>
          <div className="grid grid-cols-2 gap-1">
            <button
              type="button"
              onClick={() => {
                patchDraft({ isPlan: false });
                setPreview(null);
              }}
              className={`m-0 px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-sm border ${
                !draft.isPlan
                  ? 'border-yellow-500/60 bg-yellow-500/15 text-yellow-300'
                  : 'border-yellow-600/20 text-zinc-500 hover:text-zinc-300 hover:border-yellow-600/40'
              }`}
            >
              Actual
            </button>
            <button
              type="button"
              onClick={() => {
                patchDraft({ isPlan: true });
                setPreview(null);
              }}
              className={`m-0 px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-sm border ${
                draft.isPlan
                  ? 'border-yellow-500/60 bg-yellow-500/15 text-yellow-300'
                  : 'border-yellow-600/20 text-zinc-500 hover:text-zinc-300 hover:border-yellow-600/40'
              }`}
            >
              Plan
            </button>
          </div>
          <span className="block text-[9px] text-zinc-600 mt-0.5">
            {draft.isPlan
              ? 'Hole IDs ending in P. Collar traces only — no assays or geology.'
              : 'Drilled holes (IDs not ending in P). Default.'}
          </span>
        </div>
      </div>

      {schemaError && (
        <p className="text-[11px] text-red-400 px-1">
          {schemaError}{' '}
          <button
            type="button"
            onClick={() => setSchemaError(null)}
            className="m-0 underline text-yellow-500"
          >
            Retry
          </button>
        </p>
      )}
      {schemaLoading && !schema && (
        <p className="text-[11px] text-zinc-500 px-1 py-3 text-center">Loading collar columns…</p>
      )}

      {schema && (
        <div className="space-y-1.5">
          {draft.rules.map((node, idx) =>
            isGroup(node) && !node.field ? (
              <GroupEditor
                key={node.id || idx}
                group={node}
                schema={schema}
                dataset={draft.dataset}
                onChange={(patch) => updateNode(idx, patch)}
                onRemove={() => removeNode(idx)}
              />
            ) : (
              <RuleEditor
                key={node.id || idx}
                rule={node}
                schema={schema}
                dataset={draft.dataset}
                onChange={(patch) => updateNode(idx, patch)}
                onRemove={() => removeNode(idx)}
                canRemove={draft.rules.length > 1}
              />
            )
          )}
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => patchDraft({ rules: [...draft.rules, emptyRule()] })}
              className="m-0 flex-1 px-1.5 py-1 text-[10px] uppercase tracking-wider text-yellow-600/80 hover:text-yellow-400 border border-dashed border-yellow-600/25 rounded-sm"
            >
              + Condition
            </button>
            <button
              type="button"
              onClick={() => patchDraft({ rules: [...draft.rules, emptyGroup()] })}
              className="m-0 flex-1 px-1.5 py-1 text-[10px] uppercase tracking-wider text-yellow-600/80 hover:text-yellow-400 border border-dashed border-yellow-600/25 rounded-sm"
            >
              + Group
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-[11px] text-red-400 px-1">{error}</p>}
      {preview && (
        <p className="text-[11px] text-zinc-400 px-1">
          {preview.count.toLocaleString()} plottable hole{preview.count === 1 ? '' : 's'} match this query.
          {preview.count > 10000 ? ' Large loads may take a while.' : ''}
        </p>
      )}

      <div className="flex gap-1">
        <button
          type="button"
          onClick={runPreview}
          disabled={busy || !schema}
          className="m-0 flex-1 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm border border-yellow-600/40 text-yellow-400 hover:bg-yellow-500/10 disabled:opacity-50"
        >
          {busy ? '…' : 'Preview'}
        </button>
        <button
          type="button"
          onClick={runLoad}
          disabled={busy || !schema}
          className="m-0 flex-1 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm bg-yellow-500 text-black hover:bg-yellow-400 disabled:opacity-50"
        >
          Load holes
        </button>
        <button
          type="button"
          onClick={() => {
            resetDraft();
            setPreview(null);
            setError(null);
          }}
          className="m-0 px-2 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-300"
        >
          Reset
        </button>
      </div>

      <div className="flex gap-1">
        <input
          type="text"
          value={saveName}
          onChange={(e) => setSaveName(e.target.value)}
          placeholder={defaultSaveName}
          className={inputClass}
          aria-label="Saved filter name"
        />
        <button
          type="button"
          onClick={() => persistFilter(false)}
          disabled={!canRun || busy}
          className="m-0 shrink-0 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-sm border border-yellow-600/40 text-yellow-400 hover:bg-yellow-500/10 disabled:opacity-50"
        >
          Save
        </button>
        <button
          type="button"
          onClick={() => persistFilter(true)}
          disabled={!canRun || busy}
          className="m-0 shrink-0 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-sm bg-yellow-500 text-black hover:bg-yellow-400 disabled:opacity-50"
          title="Share with everyone on this site"
        >
          Share
        </button>
      </div>
        </>
      )}

      {loadedLayers.length > 0 && (
        <div className="pt-1 space-y-1">
          <p className="text-[9px] uppercase tracking-wider text-zinc-500 px-0.5">Loaded queries</p>
          {loadedLayers.map((layer) => {
            const status = layerStatus(layerVisibility, layer.name);
            const visible = !!(layerVisibility[layer.name]?.visible);
            return (
              <div
                key={layer.name}
                className="flex items-center gap-1 rounded-sm border border-yellow-600/15 bg-black/30 px-1.5 py-1"
              >
                <button
                  type="button"
                  onClick={() => onToggleVisibility?.(layer.name)}
                  className="m-0 flex-1 min-w-0 text-left"
                >
                  <span className="block truncate text-[11px] text-zinc-200">{layer.displayName}</span>
                  <span className="block text-[9px] text-zinc-500 uppercase tracking-wider">
                    {layer.isplan ? 'plan' : 'actual'}
                    {' · '}
                    {visible ? 'on' : status === 'cached' ? 'cached' : status}
                  </span>
                </button>
                <HoleLayerActions model={layer} />
                <button
                  type="button"
                  onClick={() => removeLoadedLayer(layer.name)}
                  className="m-0 shrink-0 px-1 text-[10px] text-zinc-500 hover:text-red-400"
                  title="Remove query"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}

      <SavedFilterList
        title="My filters"
        filters={myFilters}
        appliedFilterId={appliedFilterId}
        onApply={applySaved}
        onLoad={loadFromFilter}
        onShare={shareExisting}
        onDelete={removeFilter}
        emptyHint={filtersLoading ? 'Loading…' : 'Save a query to keep it for yourself.'}
      />
      <SavedFilterList
        title="Shared filters"
        filters={teamFilters}
        appliedFilterId={appliedFilterId}
        onApply={applySaved}
        onLoad={loadFromFilter}
        onDelete={removeFilter}
        emptyHint={filtersLoading ? 'Loading…' : 'Share a filter to make it visible to everyone.'}
      />
      <SavedFilterList
        title="Mine presets"
        filters={minePresets}
        appliedFilterId={appliedFilterId}
        onApply={applySaved}
        onLoad={loadFromFilter}
        defaultOpen={false}
        forceOpen={Boolean(String(searchText || '').trim())}
      />
    </div>
  );
}
